import { ActivityTab, JULY_TS, ReferencesTab, SnoozeUpdate } from '@fullstory/dqm-ui';
import { intl } from '@fullstory/intl';
import {
  APIEventIssueSilence,
  DQMRow,
  GetAPIEventDetailViewRequest,
  GetAPIEventDetailViewResponse,
  VolumeAggregateBy,
  VolumeTimeRange,
} from '@fullstory/proto/fs/services/dx-objects';
import { useFlags } from '@fullstory/react-hooks';
import { AppBackendRequestActions, fullstorySelectors } from '@fullstory/redux';
import { useXhrRequest } from '@fullstory/redux-util';
import { SignalStudioComponentProps, SignalStudioContext } from '@fullstory/signal-studio-context';
import jstz from 'jstz';
import React, { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DetailModalPropertiesTable } from '../../components/DetailModalPropertiesTable';
import { SidebarAPIEvent } from '../../components/SidebarAPIEvent';
import { eventObjectType, FULLSTORY_API } from '../constants';
import { APIEvent } from '../types';
import { useAPIEventsShared } from './useAPIEventsShared';
import EventSupport = DQMRow.EventSupport;

export const timeRangeToAggregation = {
  [VolumeTimeRange.DAY_30]: VolumeAggregateBy.DAY,
  [VolumeTimeRange.DAY_7]: VolumeAggregateBy.DAY,
  [VolumeTimeRange.HOUR_72]: VolumeAggregateBy.HOUR,
};

type Id = {
  identifier: 'id';
  id: string;
};

type Name = {
  identifier: 'name';
  name: string;
};

type APIEventDetailViewParams = (Id | Name) & {
  timeRange?: VolumeTimeRange;
};

interface UseAPIEventDetailViewReturn {
  data: GetAPIEventDetailViewResponse | null;
  isReady: boolean;
}

const useAPIEventDetailView = ({
  timeRange = VolumeTimeRange.DAY_30,
  ...identifier
}: APIEventDetailViewParams): UseAPIEventDetailViewReturn => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const timezone = jstz.determine().name();

  const id = identifier.identifier === 'id' ? identifier.id : null;
  const name = identifier.identifier === 'name' ? identifier.name : null;

  const req = useMemo(() => {
    if (id || name) {
      const req: GetAPIEventDetailViewRequest = {
        orgId,
        timezone,
        timeRange,
        volumeAggregateBy: timeRangeToAggregation[timeRange],
      };
      if (id) {
        req.apiEventId = id;
      } else if (name) {
        req.apiEventName = name;
      }
      return AppBackendRequestActions.getApiEventDetailView(req);
    }
    return null;
  }, [id, name, orgId, timeRange, timezone]);

  return useXhrRequest(req);
};

interface UseAPIEventsSignalStudioProps {
  apiEventId?: string; // if present, will fetch details using this api event hash id instead of name, should be the same results but allows for cache hits
  apiEvent: APIEvent;
  onCloseSignal: () => void;
  showHints?: boolean;
  onSave: (apiEventName: string, specSlugs: string[]) => void;
  onArchive: (apiEventName: string) => void;
  onUnarchive: (apiEventName: string) => void;
  snoozed?: APIEventIssueSilence;
  setSnoozed?: (update: SnoozeUpdate) => void;
}

export const useAPIEventsSignalStudio = ({
  apiEventId,
  apiEvent,
  onSave,
  onArchive,
  onUnarchive,
  onCloseSignal,
  snoozed,
  setSnoozed,
}: UseAPIEventsSignalStudioProps): {
  SignalStudio: React.ComponentType<SignalStudioComponentProps> | null;
  signalStudioProps: SignalStudioComponentProps;
} => {
  const flags = useFlags();
  const useEventReferences = flags.featureFlags().AppObjectReferencesEvents;

  const [timeRange, setTimeRange] = useState(VolumeTimeRange.DAY_7);
  const SignalStudio = useContext(SignalStudioContext);
  const { data } = useAPIEventDetailView({
    ...(apiEventId
      ? {
          identifier: 'id',
          id: apiEventId,
        }
      : {
          identifier: 'name',
          name: apiEvent.name,
        }),
    timeRange,
  });

  const {
    propertiesProps,
    save,
    archive,
    unArchive,
    disableArchive,
    selectedFsSpecObjectSlugs,
    setSelectedFsSpecObjectSlugs,
    fsSpecEnabled,
    fsSpecRoleOptions,
    areRolesDirty,
    existingSpecObjects,
  } = useAPIEventsShared(apiEvent.name, onSave, onArchive, onUnarchive);

  const previewEvent = useMemo(
    () => ({
      event: {
        primary: {
          custom: {
            named: {
              name: apiEvent.name,
            },
          },
        },
        deps: [],
      },
    }),
    [apiEvent.name],
  );

  const tabs = useMemo(() => {
    const titles = [
      intl.formatMessage({ defaultMessage: 'Properties', id: 'aI80kg' }),
      intl.formatMessage({ defaultMessage: 'Activity', id: 'ZmlNQ3' }),
    ];

    const content = [
      {
        key: 'properties',
        node: (
          <DetailModalPropertiesTable
            properties={propertiesProps.properties}
            connectionParent={apiEvent.name}
            loading={propertiesProps.propertiesLoading}
            failed={propertiesProps.propertiesFailed}
            retryFailed={propertiesProps.getConnections}
            canArchive={propertiesProps.canArchiveProperties}
            sampleValuesProps={{
              customFilter: { custom: { named: { name: apiEvent.name } } },
            }}
            existingSpecObjects={existingSpecObjects}
          />
        ),
      },
      {
        key: 'activity',
        node: (
          <ActivityTab
            activityMonitoring={data?.activityMonitoring || null}
            activityMonitoringError={data?.activityMonitoringError || null}
            timeRange={timeRange}
            onTimeRangeChange={setTimeRange}
            objectType={eventObjectType}
            monitoringStart={JULY_TS}
            auditHistory={null} // We don't have this yet for API events
            eventSupport={EventSupport.EVENT_SUPPORT_SUPPORTED}
          />
        ),
      },
    ];

    if (useEventReferences) {
      titles.push(intl.formatMessage({ defaultMessage: 'References', id: 'nCQY4D' }));
      content.push({
        key: 'references',
        node: (
          <ReferencesTab objectReferences={data?.objectReferences || null} objectType="Event" />
        ),
      });
    }

    return { titles, content };
  }, [
    data?.activityMonitoring,
    data?.activityMonitoringError,
    data?.objectReferences,
    propertiesProps.canArchiveProperties,
    propertiesProps.getConnections,
    propertiesProps.properties,
    propertiesProps.propertiesFailed,
    propertiesProps.propertiesLoading,
    timeRange,
    useEventReferences,
    apiEvent.name,
    existingSpecObjects,
  ]);

  const ConfigSidebar = (
    <SidebarAPIEvent
      apiEvent={apiEvent}
      snoozed={snoozed || null}
      setSnoozed={setSnoozed}
      eventObjectType={intl.formatMessage({ defaultMessage: 'event', id: 'bHpXg4' })}
      details={data?.details || null}
      selectedFsSpecObjectSlugs={selectedFsSpecObjectSlugs}
      setSelectedFsSpecObjectSlugs={setSelectedFsSpecObjectSlugs}
      fsSpecEnabled={fsSpecEnabled}
      fsSpecRoleOptions={fsSpecRoleOptions}
      existingSpecObjects={existingSpecObjects}
    />
  );

  const metadata = useMemo(
    () => ({
      createdBy: FULLSTORY_API,
      updatedBy: apiEvent.lastUpdatedBy || FULLSTORY_API,
      updatedAt: null, // Don't show the updated at time for API events since the time is irrelevant
      createdAt: null,
    }),
    [apiEvent.lastUpdatedBy],
  );

  return {
    SignalStudio,
    signalStudioProps: {
      header: apiEvent.name,
      configSidebar: ConfigSidebar,
      tabs,
      preview: {
        index: 0,
        onIndexChange: () => {},
        mode: 'event-preview',
        previews: [previewEvent],
      },
      lowerConfigPartName: intl.formatMessage({ defaultMessage: 'event', id: 'bHpXg4' }),
      onConfirm: areRolesDirty ? save : undefined,
      onCloseSignal,
      archived: apiEvent.archived,
      onArchive: archive,
      onUnarchive: unArchive,
      disableArchive,
      emptyMessage: null, // There is no empty state for API events
      metadata,
    },
  };
};
